// src/utils/inversionInicialUtils.js
// Utilidades para cálculo inteligente y dinámico de Inversión Inicial (Anticipo / Finiquito)

/**
 * Formatea un número como moneda en pesos mexicanos ($1,234.56)
 * @param {number} monto 
 * @returns {string} Cadena formateada
 */
export function formatearMoneda(monto) {
    const num = Number(monto) || 0;
    return `$${num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Calcula el texto de inversión inicial basado en porcentajes y el total del documento.
 * @param {number} total - Total final del documento (con o sin IVA según corresponda).
 * @param {number} pctAnticipo - Porcentaje de anticipo (por defecto 60).
 * @param {string} terminoAnticipo - Frase descriptiva de anticipo (por defecto "anticipo al autorizar").
 * @param {string} terminoConcluir - Frase descriptiva de entrega/conclusión (por defecto "al concluir").
 * @returns {string} Texto formateado con montos exactos y cuadre perfecto al centavo.
 */
export function generarTextoInversionInicial(total, pctAnticipo = 60, terminoAnticipo = 'anticipo al autorizar', terminoConcluir = 'al concluir') {
    const totalNum = Math.max(0, Number(total) || 0);
    const pct1 = Math.min(100, Math.max(0, Number(pctAnticipo) || 60));
    const pct2 = 100 - pct1;

    if (totalNum === 0) {
        if (pct2 === 0) {
            return `100% al autorizar ($0.00)`;
        }
        return `${pct1}% ${terminoAnticipo} ($0.00) · ${pct2}% ${terminoConcluir} ($0.00)`;
    }

    if (pct1 === 100) {
        return `100% al autorizar (${formatearMoneda(totalNum)})`;
    }

    // Cuadre exacto al centavo: monto1 + monto2 = totalNum exacto sin discrepancias
    const monto1 = Math.round((totalNum * (pct1 / 100)) * 100) / 100;
    const monto2 = Math.round((totalNum - monto1) * 100) / 100;

    return `${pct1}% ${terminoAnticipo} (${formatearMoneda(monto1)}) · ${pct2}% ${terminoConcluir} (${formatearMoneda(monto2)})`;
}

/**
 * Recalcula de forma inteligente una cadena existente de inversión inicial.
 * Si la cadena contiene porcentajes como "60% ... ($...) · 40% ... ($...)",
 * extrae los porcentajes y las frases descriptivas, y actualiza los montos dentro de los paréntesis
 * con el total actualizado.
 * Si la cadena está vacía o no tiene porcentajes, genera el esquema estándar 60% / 40%.
 * @param {string} textoActual - Texto actual en documento.condiciones.inversionInicial
 * @param {number} totalActual - Total numérico actual del documento
 * @returns {string} Cadena actualizada con los montos matemáticamente correspondientes
 */
export function sincronizarTextoInversionInicial(textoActual, totalActual) {
    const totalNum = Math.max(0, Number(totalActual) || 0);
    if (!textoActual || typeof textoActual !== 'string' || !textoActual.trim()) {
        return generarTextoInversionInicial(totalNum, 60);
    }

    // Expresión regular para detectar dos porcentajes con montos entre paréntesis:
    // Ej: "60% anticipo al autorizar ($2,540.40) · 40% al concluir ($1,693.60)"
    // O: "60% anticipo ($20,896) · 40% a la entrega ($13,930)"
    const regexDoblePct = /^(\d{1,2})%\s*(.*?)\s*\(\$[^)]*\)\s*([·\-/|+])\s*(\d{1,2})%\s*(.*?)\s*\(\$[^)]*\)/i;
    const match = textoActual.match(regexDoblePct);

    if (match) {
        const pct1 = parseInt(match[1], 10);
        const desc1 = match[2].trim() || 'anticipo al autorizar';
        const separador = match[3] || '·';
        const pct2 = parseInt(match[4], 10);
        const desc2 = match[5].trim() || 'al concluir';

        const monto1 = Math.round((totalNum * (pct1 / 100)) * 100) / 100;
        const monto2 = Math.round((totalNum - monto1) * 100) / 100;

        return `${pct1}% ${desc1} (${formatearMoneda(monto1)}) ${separador} ${pct2}% ${desc2} (${formatearMoneda(monto2)})`;
    }

    // Expresión regular para detectar un solo porcentaje al 100%:
    const regex100 = /^100%\s*(.*?)\s*\(\$[^)]*\)/i;
    const match100 = textoActual.match(regex100);
    if (match100) {
        const desc = match100[1].trim() || 'al autorizar';
        return `100% ${desc} (${formatearMoneda(totalNum)})`;
    }

    // Si tiene porcentajes sin montos entre paréntesis (ej: "60% anticipo y 40% entrega"):
    const regexPctSimple = /(\d{1,2})%.*?(\d{1,2})%/;
    const matchSimple = textoActual.match(regexPctSimple);
    if (matchSimple) {
        const pct1 = parseInt(matchSimple[1], 10);
        return generarTextoInversionInicial(totalNum, pct1);
    }

    // Si el texto es completamente personalizado sin porcentajes, se respeta tal cual
    return textoActual;
}
